import { useCallback, useRef, useState } from 'react';
import { Typography } from '@mui/material';
import { Content, Dialog, Footer, Header } from './useDialogAction.styles';
import { Close } from '@mui/icons-material';
import type { DefaultValues, FieldValues, UseFormReturn } from 'react-hook-form';
import DOMPurify from 'dompurify';
import type { DialogActionFormItemBehavior, UpdateAgendaRecordRes, UpdateWithNestedListRes } from 'types';
import { isMobileApp } from 'utils/common';
import { IconButton } from 'components/mui/common/IconButton';
import { Button } from 'components/mui/common/Button';
import { Icon } from 'components/mui/common/Icon';

type RecordResponse = UpdateAgendaRecordRes | UpdateAgendaRecordRes[] | UpdateWithNestedListRes | undefined;

export interface DialogActionResult {
  behavior: DialogActionFormItemBehavior;
  buttonId: string;
}

export const useDialogAction = (form: UseFormReturn) => {
  const mobileApp = isMobileApp();
  const [dialogAction, setDialogAction] = useState<UpdateAgendaRecordRes['dialogAction'] | null>(null);

  const resolveRef = useRef<((value: DialogActionResult | null) => void) | null>(null);
  const oldValuesRef = useRef<DefaultValues<FieldValues> | null>(null);

  const getDialogAction = useCallback((response: RecordResponse): UpdateAgendaRecordRes['dialogAction'] | null => {
    if (!response) return null;
    if (Array.isArray(response)) {
      for (const res of response) {
        const action = getDialogAction(res);
        if (action) return action;
      }
      return null;
    }
    const mainRecordResponse = 'mainRecord' in response ? response.mainRecord : response;
    if (mainRecordResponse?.dialogAction) return mainRecordResponse.dialogAction;

    const nestedRecordsResponse = 'nestedRecords' in response ? response.nestedRecords : [];
    for (const record of nestedRecordsResponse) {
      const action = getDialogAction(record);
      if (action) return action;
    }
    return null;
  }, []);

  const handleDialogAction = useCallback(
    (response: RecordResponse, oldValues?: DefaultValues<FieldValues>) => {
      const action = getDialogAction(response);
      if (action) {
        oldValuesRef.current = oldValues || null;
        setDialogAction(action);

        return new Promise<DialogActionResult | null>((resolve) => {
          resolveRef.current = resolve;
        });
      }
      return null;
    },
    [getDialogAction]
  );

  const handleClick = useCallback(
    (id: string, behavior: DialogActionFormItemBehavior) => () => {
      if (behavior === 'DiscardAndSave') {
        if (oldValuesRef.current) {
          form.reset(oldValuesRef.current);
        } else {
          form.reset();
        }
      }

      setDialogAction(null);

      if (resolveRef.current) {
        resolveRef.current({ behavior, buttonId: id });
        resolveRef.current = null;
      }
      oldValuesRef.current = null;
    },
    [form]
  );

  const handleClose = useCallback(() => {
    setDialogAction(null);

    if (resolveRef.current) {
      resolveRef.current(null);
      resolveRef.current = null;
    }
    oldValuesRef.current = null;
  }, []);

  const Component = dialogAction ? (
    <Dialog disableScrollLock disableEnforceFocus open={Boolean(dialogAction)} onClose={handleClose} fullWidth mobileApp={mobileApp}>
      <Header>
        <div>
          {dialogAction.icon && <Icon icon={dialogAction.icon} />}
          <Typography variant="h5">{dialogAction.title}</Typography>
        </div>
        <IconButton onClick={handleClose}>
          <Close />
        </IconButton>
      </Header>

      <Content mobileApp={mobileApp}>
        <Typography dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(dialogAction.description || '') }} />
      </Content>

      <Footer>
        {dialogAction.actionableFormItems?.map((btn) => (
          <Button key={btn.formItem.id} variant="contained" onClick={handleClick(btn.formItem.id!, btn.behavior)}>
            {btn.formItem.title || btn.formItem.name}
          </Button>
        ))}
      </Footer>
    </Dialog>
  ) : null;

  return [handleDialogAction, Component] as const;
};
